<template>
  <div>
    <StepHeader
      :title="stepTitle"
    />

    <StepContent
      :width="stepWidth"
    >
      <AppInput
        :label="incomeLabel"
        :hint="incomeHint"
        :type="type"
        :pattern="pattern"
        :name="fieldName"
        :value="incomeValue"
        :error="errorIncomeValue"
        :is-hourly-wage="isHourly"
        icon
        v-data-layer
        :data-layer="incomeDataLayer"
        @input="handleInput('income_value', $event)"
      >
        <span slot="icon">$</span>
      </AppInput>

      <!-- Hours per Week -->
      <AppInput
        v-if="isHourly"
        label="Average Hours per Week"
        type="tel"
        pattern="\d*"
        name="income_hours"
        :value="incomeHours"
        :error="errorIncomeHours"
        v-data-layer
        data-layer="hour_week_field"
        @input="handleInput('income_hours', $event)"
      />
    </StepContent>

    <StepFooter>
      <AppButtonContinue
        v-data-layer
        :data-layer="datalayerContinue"
        @click="handleContinue()"
      >
        Continue
      </AppButtonContinue>

      <StepTooltip>
        Your income details help us make sure your vehicle payments are easy and affordable.
      </StepTooltip>
    </StepFooter>
  </div>
</template>

<script>
import AppButtonContinue from '@/components/common/buttons/AppButtonContinue'
import AppInput from '../inputs/AppInput'
import StepContent from '@/components/common/step/StepContent'
import StepFooter from '@/components/common/step/StepFooter'
import StepHeader from '@/components/common/step/StepHeader'
import StepTooltip from '@/components/common/step/StepTooltip'
import validation from '@/mixins/validation'

export default {
  // Name
  name: 'employment-income',

  // Components
  components: {
    AppButtonContinue,
    AppInput,
    StepContent,
    StepFooter,
    StepHeader,
    StepTooltip
  },

  // Mixins
  mixins: [validation],

  // Data
  data () {
    return {
      datalayerStepName: 'emp_income',
      fieldName: 'monthly_income',
      defaultSelected: true,
      submitted: false,
      errors: {
        isRequired: 'Required.',
        invalidNumber: 'Invalid number'
      }
    }
  },

  // Computed
  computed: {
    errorIncomeValue () {
      const value = this.incomeValue
      if (!this.submitted && !value) return ''
      if (!value) return this.errors.isRequired
      if (!this.isValidNumber(value)) return this.errors.invalidNumber
    },

    errorIncomeHours () {
      const value = this.incomeHours
      if (this.incomeType !== 'Hourly' || (!this.submitted && !value)) return ''
      if (!value) return this.errors.isRequired
      if (!this.isValidNumber(value)) return this.errors.invalidNumber
    },

    incomeValue () {
      return this.$store.state.application.income_value
    },

    incomeHours () {
      return this.$store.state.application.income_hours
    },

    incomeType () {
      return this.$store.state.application.income_type
    },

    incomeLabel () {
      switch (this.incomeType) {
        case ('Annual'):
          return 'Annual Salary'
        case ('Hourly'):
          return 'Hourly Wage'
        default:
          return 'Monthly Income'
      }
    },

    // incomePlaceholder () {
    //   switch (this.incomeType) {
    //     case ('Annual'):
    //       return 'Annual Salary'
    //     case ('Hourly'):
    //       return 'Hourly Wage'
    //     default:
    //       return 'Monthly Income'
    //   }
    // },

    type () {
      return this.isHourly ? 'number' : 'tel'
    },

    pattern () {
      return this.isHourly ? '' : '\\d*'
    },

    incomeHint () {
      if (this.isHourly) return
      return 'Round to Nearest Dollar'
    },

    incomeDataLayer () {
      switch (this.incomeType) {
        case ('Annual'):
          return 'annual_income_field'
        case ('Hourly'):
          return 'hour_wage_field'
        default:
          return 'monthly_income_field'
      }
    },

    isHourly () {
      return this.incomeType === 'Hourly'
    },

    monthly_income () {
      return this.$store.state.application.monthly_income
    },

    datalayerContinue () {
      return `${this.datalayerStepName}_continue`
    },

    stepTitle () {
      switch (this.incomeType) {
        case ('Hourly'):
          return 'How Much Do You Get Paid per Hour?'
        case ('Annual'):
          return 'What Is Your Annual Salary?\nBefore Taxes & Deductions'
        default:
          return 'What Is Your Monthly Income?\nBefore Taxes & Deductions'
      }
    },

    stepWidth () {
      return this.isHourly ? 'md' : 'xs'
    },

    stepIsValid () {
      return !this.errorIncomeValue && !this.errorIncomeHours
    }
  },

  // Methods
  methods: {
    /**
     * Is Valid Number
     * Checks if the value is valid
     * If the user has Hourly selected, allow a decimal followed by 2 digits
     * @returns {boolean}
     */
    isValidNumber (value) {
      if (parseFloat(value) > 999999999) return false

      return this.isHourly
        ? /^\d+(?:\.\d{0,2})?$/.test(value)
        : /^\d+$/.test(value)
    },

    /**
     * Get Monthly Income
     * calculate the user's monthly based on their incomeType
     * @returns {number}
     */
    getMonthlyIncome () {
      switch (this.incomeType) {
        case ('Annual'):
          return +(+this.incomeValue / 12).toFixed(2)
        case ('Hourly'):
          return +(+this.incomeHours * +this.incomeValue * 52 / 12).toFixed(2)
        default:
          return +this.incomeValue
      }
    },

    /**
     * Handle Input
     * Updates a field and this.monthly_income
     * @param string - name of the field
     * @param string - value
     */
    handleInput (field, value) {
      this.$store.updateApplication(field, value)
      this.$store.updateApplication('monthly_income', this.getMonthlyIncome())
    },

    /**
     * Handle Continue
     * Validate the step and continue
     */
    async handleContinue () {
      this.submitted = true
      if (this.stepIsValid) {
        this.$emit('next')
      }
    }
  }
}
</script>

<style scoped lang="scss">
.step-footer {
  margin-top: 30px;

  @include breakpoint('tablet-portrait-up') {
    margin-top: 0;
  }
}
</style>
